import { ChangeEvent, useEffect, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button } from '@mesh.ai/rabbit';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RestrictAccessContainer } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/SectionWrapper';

import { AutoResizeInput } from 'uiToolkitV1/AutoResizeInput';
import { BottomDrawer, DrawerContent, DrawerFooter, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { TextHeader3, TextLabel } from 'uiToolkitV1/Typography';

import { AnswerTypeDropdown } from './components/AnswerTypeDropdown';
import { CanAddCommentDropdown } from './components/CanAddCommentDropdown';
import { ChoiceBasedScale } from './components/ChoiceBasedScale';
import { DriverDropdown } from './components/DriverDropdown';
import { EmojiScale } from './components/EmojiScale';
import { QuestionPreview } from './components/QuestionPreview';
import { ScaleBasedScore } from './components/ScaleBasedScore';
import { ScaleValueDropdown } from './components/ScaleValueDropdown';
import { TemplateDropdown } from './components/TemplateDropdown';
import { MAX_OPTIONS_FOR_NPS, OPEN_ENGAGEMENT_QUESTION_PANEL } from './constants/AddQuestionConstant';
import {
  useGetEngagementDriversList,
  useGetEngagementQuestionById,
  useMutateAddEngagementQuestion,
} from './hooks/dataHooks';
import {
  EngagementQuestionType,
  initializeEngagementAddQuestion,
  updateEngagementAddQuestion,
  useAddEngagementQuestion,
  useIsAddQuestionDrawerVisible,
} from './slice/AddEngagementQuestion.slice';

const Footer = ({ onClose, onSave, isSaveDisabled = false }) => (
  <Flex justifyContent="space-between" w="100%">
    <Button variant="outline" onClick={onClose}>
      Close
    </Button>
    <Button onClick={onSave} disabled={isSaveDisabled}>
      Save
    </Button>
  </Flex>
);

export const AddQuestionBottomDrawer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();

  const { isDrawerOpen, action, questionId } = useIsAddQuestionDrawerVisible();
  const {
    description,
    answerType,
    maxOptions,
    answerOptions,
    scaleType,
    enableFeedbackText,
    isTextRequired,
    tag,
    isEditable,
    ...rest
  } = useAddEngagementQuestion();

  const isEditOrCopyAction = ['edit', 'copy'].includes(action);
  const isCopyAction = action === 'copy';

  const isRestrictAccess = useMemo(() => isEditable === false && action === 'edit', [isEditable, action]);

  const { data: existingQuestion, isLoading } = useGetEngagementQuestionById({
    questionId: +questionId,
    enabled: isEditOrCopyAction,
  });

  const { data: drivers } = useGetEngagementDriversList();

  const isNPS = useMemo(
    () => answerType === 'NUMBER_RATING' && maxOptions === MAX_OPTIONS_FOR_NPS,
    [answerType, maxOptions]
  );

  const filteredDrivers = useMemo(
    () => (isNPS ? drivers?.filter(({ tagSubType }) => ['ENPS', 'MNPS'].includes(tagSubType)) : drivers),
    [drivers, isNPS]
  );

  const { mutate: addEngagementQuestion } = useMutateAddEngagementQuestion();

  const handleCloseDrawer = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('questionId');
    queryParams.delete('action');
    queryParams.delete('source');
    queryParams.delete(OPEN_ENGAGEMENT_QUESTION_PANEL);
    history.push({
      search: queryParams.toString(),
    });
    dispatch(initializeEngagementAddQuestion(null));
  };

  const handleQuestionChange = (value: Partial<EngagementQuestionType>) => {
    dispatch(
      updateEngagementAddQuestion({
        ...value,
      })
    );
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updatedAnswerOptions = answerOptions.map((option, i) =>
      i === index ? { ...option, description: value } : option
    );
    handleQuestionChange({ answerOptions: updatedAnswerOptions });
  };

  const isSaveDisabled = useMemo(() => {
    if (description === '') return true;
    if (!tag?.uuid) return true;
    return false;
  }, [description, tag]);

  const onSave = () => {
    addEngagementQuestion({
      question: {
        description,
        answerType,
        maxOptions,
        answerOptions,
        scaleType,
        enableFeedbackText,
        isTextRequired,
        tag,
        ...rest,
        // @ts-expect-error - questionId is optional
        id: isCopyAction ? undefined : questionId,
      },
    });
    handleCloseDrawer();
    toast({
      title: isCopyAction ? 'Question duplicated successfully' : 'Question saved successfully',
      status: 'success',
    });
  };

  useEffect(() => {
    if (isEditOrCopyAction && !isLoading && existingQuestion) {
      dispatch(
        initializeEngagementAddQuestion({
          ...existingQuestion,
          scaleType: null,
        })
      );
    }
  }, [existingQuestion, isLoading, isEditOrCopyAction, dispatch]);

  return (
    <BottomDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer}>
      <DrawerContent border="4px solid" borderColor="borders.EBEFF3">
        <DrawerHeader onClose={handleCloseDrawer}>
          <Flex p="16px 8px 0px 8px" gap="8px">
            <Flex gap="4px" pb="8px" borderBottom="1px solid" borderColor="#E7E7E7" w="full">
              <TextHeader3 bold color="text.g1">
                {/* eslint-disable-next-line no-nested-ternary */}
                {action === 'edit' ? 'Edit' : action === 'copy' ? 'Duplicate' : 'Add'} Question
              </TextHeader3>
            </Flex>
          </Flex>
          {isRestrictAccess && (
            <Box bg="#fff1e4" p="12px">
              <TextLabel color="#c62c02">
                This question is being used in a survey. You cannot edit this question but you can create a duplicate of
                the question and edit it.
              </TextLabel>
            </Box>
          )}
        </DrawerHeader>
        <Flex flexDir="column" overflowY="auto" h="100%">
          {/* Body */}
          <RestrictAccessContainer restrictAccess={isRestrictAccess}>
            <Flex p="8px" gap="8px" flexDir="column">
              <Flex flexDir="column" p="8px" mt="8px" gap="4px">
                {/* Response text field */}
                <Flex flexDir="column" gap="4px">
                  <AutoResizeInput
                    placeholder="Enter your question"
                    isDashed
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                      handleQuestionChange({
                        description: e.target.value,
                      });
                    }}
                    value={description}
                  />
                </Flex>
                {/* Label fields */}
                <Flex flexDir="column" gap="8px">
                  <AnswerTypeDropdown drivers={drivers} handleQuestionChange={handleQuestionChange} />
                  {/* scale value */}
                  {answerType !== 'NONE' && <ScaleValueDropdown handleQuestionChange={handleQuestionChange} />}
                  {/* type */}
                  {(answerType === 'NUMBER_RATING' && maxOptions === MAX_OPTIONS_FOR_NPS) ||
                  answerType === 'NONE' ||
                  answerType === 'DROP_DOWN' ? null : (
                    <TemplateDropdown handleQuestionChange={handleQuestionChange} />
                  )}
                  {answerType === 'EMOJI' && (
                    <EmojiScale
                      scale={maxOptions as 3 | 4 | 5}
                      answerOptions={answerOptions}
                      onDescriptionChange={handleDescriptionChange}
                    />
                  )}
                  {answerType === 'NUMBER_RATING' && (
                    <ScaleBasedScore answerOptions={answerOptions} onDescriptionChange={handleDescriptionChange} />
                  )}
                  {answerType === 'DROP_DOWN' && (
                    <ChoiceBasedScale answerOptions={answerOptions} onDescriptionChange={handleDescriptionChange} />
                  )}
                  {/* Add comment to question */}
                  {answerType !== 'NONE' && <CanAddCommentDropdown handleQuestionChange={handleQuestionChange} />}
                  {/* Engagement driver */}
                  <DriverDropdown
                    filteredDrivers={filteredDrivers}
                    handleQuestionChange={handleQuestionChange}
                    tag={tag}
                  />
                </Flex>
                <QuestionPreview />
              </Flex>
            </Flex>
          </RestrictAccessContainer>
        </Flex>
        <DrawerFooter>
          <Footer isSaveDisabled={isSaveDisabled || isRestrictAccess} onSave={onSave} onClose={handleCloseDrawer} />
        </DrawerFooter>
      </DrawerContent>
    </BottomDrawer>
  );
};
